import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Container,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { skipToken } from '@reduxjs/toolkit/query';

import { TreeExplorer, type TreeNode } from '../../shared/components/TreeExplorer';
import { useAuth } from '../../shared/components/AuthProvider';
import { useConfirm } from '../../shared/components/ConfirmProvider';

import {
  useGetChildrenQuery,
  useLazyGetChildrenQuery,
  useCreateCustomStandardMutation,
  useUpdateCustomStandardMutation,
  useMoveCustomStandardMutation,
  useDeleteCustomStandardMutation,
} from './customStandardsApi';
import { WarehouseInsertDialog } from './components/WarehouseInsertDialog';

import type { CustomStandardBase, UUID } from '@/shared/types/customStandard';

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

// Simple create/edit dialog
function CreateEditDialog({
  open,
  title,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  initial?: { name?: string; code?: string };
  onClose: () => void;
  onSubmit: (values: { name: string; code: string }) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setCode(initial?.code ?? '');
    }
  }, [open, initial?.name, initial?.code]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Title"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label="Code (optional)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g., CS.1 or custom identifier"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit({ name: name.trim(), code: code.trim() })}
          variant="contained"
          disabled={!name.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Map DB rows to TreeExplorer nodes
function mapToTreeNodes(rows: CustomStandardBase[]): TreeNode[] {
  return rows.map((r) => ({
    id: r.id,
    label: r.title,
    type: 'custom-standard',
    hasChildren: true, // let the loader decide if there are any
    metadata: {
      code: r.code ?? undefined,
      parent_id: r.parent_id ?? null,
      created_at: r.created_at,
      updated_at: r.updated_at,
      path: r.path,
    },
  }));
}

export default function CustomStandardsPage() {
  const { user, initializing: authLoading } = useAuth();
  const confirm = useConfirm();
  const ownerId = user?.id;

  // Root-level collections (parent_id null)
  const rootArg = useMemo(() => {
    if (!ownerId) return skipToken;
    return { ownerId, parentId: null as UUID | null };
  }, [ownerId]);
  const {
    data: rootRows = [],
    isFetching: rootLoading,
    refetch: refetchRoot,
  } = useGetChildrenQuery(rootArg);

  // Track labels for all selected path ancestors (for breadcrumb)
  const [pathLabels, setPathLabels] = useState<Map<string, string>>(new Map());

  const rootNodes = useMemo(() => mapToTreeNodes(rootRows), [rootRows]);

  // Seed path labels whenever root nodes change
  useEffect(() => {
    if (rootNodes.length === 0) return;
    setPathLabels((prev) => {
      const next = new Map(prev);
      for (const n of rootNodes) next.set(n.id, n.label);
      return next;
    });
  }, [rootNodes]);

  // Lazy loader for children columns
  const [loadChildren] = useLazyGetChildrenQuery();

  // Mutations
  const [createNode, { isLoading: creating }] = useCreateCustomStandardMutation();
  const [updateNode, { isLoading: updating }] = useUpdateCustomStandardMutation();
  const [moveNode, { isLoading: moving }] = useMoveCustomStandardMutation();
  const [deleteNode, { isLoading: deleting }] = useDeleteCustomStandardMutation();

  // Current selection state (from TreeExplorer callback)
  const [selected, setSelected] = useState<{ node: TreeNode; path: string[] } | null>(null);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  // Force TreeExplorer to rebuild columns without changing the selection path
  const [refreshKey, setRefreshKey] = useState(0);

  // Snackbar for user feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const showSnack = (message: string, severity: 'success' | 'error' | 'info' = 'info') =>
    setSnackbar({ open: true, message, severity });

  // Siblings of selected node for move up/down, based on path
  const siblingsParentId = useMemo<UUID | null>(() => {
    if (!selectedPath.length) return null;
    return (selectedPath.length > 1 ? selectedPath[selectedPath.length - 2] : null) as UUID | null;
  }, [selectedPath]);

  const siblingsArg = useMemo(() => {
    if (!ownerId) return skipToken;
    return { ownerId, parentId: siblingsParentId };
  }, [ownerId, siblingsParentId]);

  const { data: siblingRows = [], isFetching: siblingsLoading } = useGetChildrenQuery(siblingsArg);

  const siblings = siblingRows;

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createParent, setCreateParent] = useState<string | null>(null);

  const onAddCollection = () => {
    setCreateParent(null);
    setCreateOpen(true);
  };

  const onAddChild = () => {
    if (!selected) return;
    setCreateParent(selected.node.id);
    setCreateOpen(true);
  };

  const handleCreate = async (values: { name: string; code: string }) => {
    if (!ownerId) return;
    const parentId = createParent ?? null;

    try {
      const created = await createNode({
        ownerId,
        title: values.name,
        parentId,
        code: values.code || null,
      }).unwrap();

      if (parentId) {
        await loadChildren({ ownerId, parentId }, true).unwrap();
        setSelectedPath([...selectedPath, created.id]);
      } else {
        await refetchRoot();
        setSelectedPath([created.id]);
      }

      setCreateOpen(false);
      setCreateParent(null);
      showSnack('Standard created', 'success');
    } catch (err) {
      console.error('Create failed:', err);
      showSnack('Failed to create standard. Please try again.', 'error');
    }
  };

  const onNodeSelect = useCallback((node: TreeNode, path: string[]) => {
    setSelected({ node, path });
    setSelectedPath(path);
    // Build label map for breadcrumb — node label is the last entry
    setPathLabels((prev) => {
      const next = new Map(prev);
      next.set(node.id, node.label);
      return next;
    });
  }, []);

  const onLoadChildren = useCallback(
    async (node: TreeNode, _path: string[]) => {
      if (!ownerId) return [];
      // Track parent label for breadcrumbs
      setPathLabels((prev) => {
        const next = new Map(prev);
        next.set(node.id, node.label);
        return next;
      });
      const rows = await loadChildren({ ownerId, parentId: node.id }, true).unwrap();
      const nodes = mapToTreeNodes(rows);
      // Track child labels
      setPathLabels((prev) => {
        const next = new Map(prev);
        for (const n of nodes) next.set(n.id, n.label);
        return next;
      });
      return nodes;
    },
    [ownerId, loadChildren]
  );

  const handleDelete = async () => {
    if (!selectedRow || !ownerId) return;
    const ok = await confirm({
      title: 'Delete standard?',
      message: 'This will move the item to trash. Continue?',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!ok) return;

    const parentPath = selectedPath.slice(0, -1);
    const deletingId = selectedRow.id;
    const deletingParentId = selectedRow.parent_id ?? null;

    try {
      await deleteNode({
        id: deletingId,
        hard: false,
        parentId: deletingParentId,
        ownerId,
      }).unwrap();

      // Only update selection after confirmed server success
      setSelectedPath(parentPath);
      if (parentPath.length === 0) {
        setSelected(null);
      }

      if (parentPath.length) {
        const parentId = parentPath[parentPath.length - 1] as UUID;
        await loadChildren({ ownerId, parentId }, true).unwrap();
      } else {
        await refetchRoot();
      }

      showSnack('Standard deleted', 'success');
    } catch (err) {
      console.error('Delete failed:', err);
      showSnack('Failed to delete. Please try again.', 'error');
    }
  };

  // Derive selectedRow from selectedPath and caches; fall back to selected
  const selectedRow = useMemo(() => {
    const idFromPath = selectedPath[selectedPath.length - 1];
    const fallbackId = selected?.node.id;
    const id = idFromPath ?? fallbackId;
    if (!id) return undefined;

    // Try siblings cache first
    const match = siblings.find((s) => s.id === id);
    if (match) return match;

    // Try root cache if selecting a root item
    if (selectedPath.length === 1 && rootRows.length) {
      const rootMatch = rootRows.find((r) => r.id === id);
      if (rootMatch) return rootMatch;
    }

    // Last resort: if we have the old selected node, build a minimal placeholder
    if (selected?.node && selected?.node.id === id) {
      const md = selected.node.metadata || {};
      return {
        id,
        title: selected.node.label,
        code: md.code ?? null,
        parent_id: md.parent_id ?? null,
        created_at: md.created_at ?? new Date().toISOString(),
        updated_at: md.updated_at ?? new Date().toISOString(),
        path: md.path ?? '',
        label: 0,
        position: 0,
        standards_warehouse_id: null,
        additional_content: null,
        owner_id: ownerId!,
        is_deleted: false,
      } as CustomStandardBase;
    }

    return undefined;
  }, [selected, selectedPath, siblings, rootRows, ownerId]);

  // Inline edit state for the details panel
  const [editTitle, setEditTitle] = useState('');
  const [editCode, setEditCode] = useState('');

  // Synchronize when selection changes
  useEffect(() => {
    setEditTitle(selectedRow?.title ?? '');
    setEditCode(selectedRow?.code ?? '');
  }, [selectedRow?.id, selectedRow?.title, selectedRow?.code]);

  const handleSaveEdits = async () => {
    if (!selectedRow || !ownerId) return;
    const parentIdHint = (
      selectedPath.length > 1 ? selectedPath[selectedPath.length - 2] : null
    ) as UUID | null;

    try {
      // fire the optimistic update
      await updateNode({
        id: selectedRow.id,
        title: editTitle.trim(),
        code: editCode.trim() || null,
        parentId: parentIdHint,
        ownerId: ownerId!,
      }).unwrap();

      // make TreeExplorer re-read the (now patched) cache
      setRefreshKey((k) => k + 1);
      showSnack('Changes saved', 'success');
    } catch (err) {
      console.error('Save failed:', err);
      showSnack('Failed to save changes. Please try again.', 'error');
      // Reset local edit state to match what's in the cache
      setEditTitle(selectedRow.title);
      setEditCode(selectedRow.code ?? '');
    }
  };

  const handleMoveUp = async () => {
    if (!selectedRow || !ownerId) return;
    const idx = siblings.findIndex((s) => s.id === selectedRow.id);
    if (idx <= 0) {
      if (idx === 0) showSnack('Already at the top', 'info');
      return;
    }
    try {
      const prev = siblings[idx - 1];
      await moveNode({
        id: selectedRow.id,
        newParentId: siblingsParentId,
        afterId: prev.id,
        ownerId,
        oldParentId: siblingsParentId,
      }).unwrap();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Move up failed:', err);
      showSnack('Failed to reorder. Please try again.', 'error');
    }
  };

  const handleMoveDown = async () => {
    if (!selectedRow || !ownerId) return;
    const idx = siblings.findIndex((s) => s.id === selectedRow.id);
    if (idx < 0 || idx >= siblings.length - 1) {
      if (idx === siblings.length - 1) showSnack('Already at the bottom', 'info');
      return;
    }
    try {
      const next = siblings[idx + 1];
      await moveNode({
        id: selectedRow.id,
        newParentId: siblingsParentId,
        beforeId: next.id,
        ownerId,
        oldParentId: siblingsParentId,
      }).unwrap();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Move down failed:', err);
      showSnack('Failed to reorder. Please try again.', 'error');
    }
  };

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importParent, setImportParent] = useState<UUID | null>(null);

  const onOpenImportAtRoot = () => {
    setImportParent(null);
    setImportOpen(true);
  };
  const onOpenImportUnderSelected = () => {
    if (!selectedRow) return;
    setImportParent(selectedRow.id as UUID);
    setImportOpen(true);
  };

  const handleAfterImport = async (createdIds: string[]) => {
    if (!ownerId) return;
    if (importParent) {
      await loadChildren({ ownerId, parentId: importParent }, true).unwrap();
      setSelectedPath([...(selectedPath.length ? selectedPath : []), createdIds[0]]);
    } else {
      await refetchRoot();
      setSelectedPath([createdIds[0]]);
    }
    setRefreshKey((k) => k + 1);
  };

  const busy =
    authLoading || rootLoading || creating || updating || moving || deleting || siblingsLoading;

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
          <Box>
            <Typography variant="h2" component="h1">
              My Standards
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={onOpenImportAtRoot}
            >
              Import from warehouse
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAddCollection}>
              New collection
            </Button>
          </Box>
        </Box>
        {/* Header divider and bottom spacing */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }} />
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Paper
            sx={{
              display: 'flex',
              minHeight: 480,
              height: { xs: 520, md: 640 },
              overflow: 'hidden',
            }}
          >
            {/* Left: Tree columns */}
            <Box sx={{ flex: 1, minWidth: 320, borderRight: 1, borderColor: 'divider' }}>
              {busy && rootNodes.length === 0 ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : rootNodes.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    No collections yet
                  </Typography>

                  <Button
                    onClick={onAddCollection}
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{ mt: 1 }}
                  >
                    Create your first collection
                  </Button>
                </Box>
              ) : (
                <TreeExplorer
                  rootNodes={rootNodes}
                  onNodeSelect={onNodeSelect}
                  onLoadChildren={onLoadChildren}
                  isLoading={busy}
                  showMetadata={false}
                  height={640}
                  columnWidth={260}
                  selectedPath={selectedPath}
                  refreshKey={refreshKey}
                  searchable
                />
              )}
            </Box>

            {/* Right: Details & actions */}
            {selectedRow && (
              <Box sx={{ width: { xs: 320, md: 380 }, p: 2 }}>
                {/* Breadcrumb trail */}
                {selectedPath.length > 1 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    {selectedPath
                      .slice(0, -1)
                      .map((id) => pathLabels.get(id) || '…')
                      .join(' › ')}
                  </Typography>
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  Details
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={2}>
                  <TextField
                    label="Title"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Code (optional)"
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value)}
                    fullWidth
                  />

                  <Stack direction="row" spacing={1}>
                    <Button
                      startIcon={
                        updating ? <CircularProgress size={16} /> : <DriveFileRenameOutlineIcon />
                      }
                      variant="contained"
                      onClick={handleSaveEdits}
                      disabled={!editTitle.trim() || updating}
                    >
                      {updating ? 'Saving…' : 'Save changes'}
                    </Button>
                    <Button
                      startIcon={<SubdirectoryArrowRightIcon />}
                      onClick={onAddChild}
                      variant="outlined"
                    >
                      Add sub-standard
                    </Button>
                    <Button
                      startIcon={<FileDownloadIcon />}
                      onClick={onOpenImportUnderSelected}
                      variant="outlined"
                    >
                      Insert here
                    </Button>
                  </Stack>

                  <Divider />

                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton onClick={handleMoveUp} disabled={moving}>
                          {moving ? <CircularProgress size={18} /> : <ArrowUpwardIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton onClick={handleMoveDown} disabled={moving}>
                          {moving ? <CircularProgress size={18} /> : <ArrowDownwardIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Box flex={1} />
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={handleDelete} disabled={deleting}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Info
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {!!selectedRow.code && (
                        <Typography variant="body2">Code: {selectedRow.code}</Typography>
                      )}
                      {selectedRow.created_at && (
                        <Tooltip title={new Date(selectedRow.created_at).toLocaleString()} arrow>
                          <Typography variant="body2" color="text.secondary">
                            Created: {formatRelativeTime(selectedRow.created_at)}
                          </Typography>
                        </Tooltip>
                      )}
                      {selectedRow.updated_at && (
                        <Tooltip title={new Date(selectedRow.updated_at).toLocaleString()} arrow>
                          <Typography variant="body2" color="text.secondary">
                            Updated: {formatRelativeTime(selectedRow.updated_at)}
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            )}
          </Paper>

          <CreateEditDialog
            open={createOpen}
            title={createParent ? 'Add sub-standard' : 'New collection'}
            onClose={() => setCreateOpen(false)}
            onSubmit={handleCreate}
          />

          {ownerId && (
            <WarehouseInsertDialog
              open={importOpen}
              ownerId={ownerId}
              targetParentId={importParent}
              onClose={() => setImportOpen(false)}
              onInserted={handleAfterImport}
            />
          )}
        </Stack>

        {/* Feedback snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
